<template>
    <div>
        <x-layout>
            <x-flex>
                <x-select
                    data-test="insurer"
                    v-model="insurerData.insurer_company_id"
                    :items="availableInsurers"
                    class="required"
                    label="Insurer"
                    item-text="name"
                    item-value="id"
                    placeholder=" "
                    append-icon="keyboard_arrow_down"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                    :rules="[v => ruleRequired(v, 'Insurer')]"
                />
            </x-flex>

            <x-flex class="grow-0">
                <x-btn
                    class="square-icon label-spacing"
                    color="tertiary"
                    small
                    @click="$emit('deleteInsurer')"
                >
                    <i class="material-icons-outlined">delete_forever</i>
                </x-btn>
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-select
                    data-test="underwriter"
                    v-model="insurerData.underwriter_id"
                    :items="availableUnderwriters"
                    class="required"
                    label="Underwriter"
                    item-text="name"
                    item-value="id"
                    placeholder=" "
                    append-icon="keyboard_arrow_down"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                    :rules="[v => ruleRequired(v, 'Underwriter')]"
                />
            </x-flex>

            <x-flex xs6>
                <x-select
                    data-test="security"
                    v-model="insurerData.security"
                    :items="availableSecurities"
                    class="required"
                    label="Security"
                    item-text="name"
                    placeholder=" "
                    return-object
                    append-icon="keyboard_arrow_down"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                    :rules="[v => ruleRequired(v.id, 'Security')]"
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-text-field
                    v-model="strengthRating"
                    label="Security rating"
                    disabled
                />
            </x-flex>

            <x-flex xs6>
                <x-text-field
                    v-model="insurerData.security.max_tenor"
                    label="Max tenor"
                    disabled
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-currency-text-field
                    v-model="insurerData.security.max_line_size"
                    label="Max line size"
                    :prefix="insurerData.security.max_line_currency"
                    disabled
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-select
                    data-test="indicated_line_size_currency"
                    v-model="insurerData.indicated_line_size_currency_id"
                    :items="currencies"
                    :class="{ 'required': _required(insurerData.indicated_line_size) }"
                    label="Indicated line size currency"
                    item-value="id"
                    item-text="name"
                    placeholder=" "
                    append-icon="keyboard_arrow_down"
                    :menu-props="{ offsetY: true }"
                    :rules="[v => ruleRequiredIf(v, _required(insurerData.indicated_line_size), true)]"
                />
            </x-flex>

            <x-flex xs6>
                <x-currency-text-field
                    data-test="indicated_line_size"
                    v-model="insurerData.indicated_line_size"
                    :class="{ 'required': _required(insurerData.indicated_line_size_currency_id) }"
                    label="Indicated line size"
                    placeholder=" "
                    :prefix="indicatedLineSizeCurrency"
                    optional
                    :rules="[v => ruleRequiredIf(v, _required(insurerData.indicated_line_size_currency_id), true)]"
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-select
                    data-test="pricing_type"
                    v-model="insurerData.pricing_type"
                    :items="pricingTypes"
                    label="Pricing type"
                    item-text="name"
                    item-value="id"
                    placeholder=" "
                    append-icon="keyboard_arrow_down"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                />
            </x-flex>

            <x-flex v-if="['fixed_rate', 'variable'].includes(insurerData.pricing_type)" xs6>
                <x-text-field
                    data-test="price"
                    v-model="insurerData.price"
                    class="required"
                    suffix="BPS"
                    label="Price"
                    placeholder=" "
                    hide-details
                    :rules="[
                        v => ruleRequired(v, 'Price'),
                        v => ruleNumeric(v, false, false, true),
                    ]"
                />
            </x-flex>

            <x-flex v-if="['flat_fee'].includes(insurerData.pricing_type)" xs6>
                <x-currency-text-field
                    v-model="insurerData.price"
                    class="required"
                    label="Price"
                    placeholder=" "
                    :prefix="riskCurrency"
                    :rules="[
                        v => ruleRequired(v, 'Price'),
                        v => ruleNumeric(v, false, false, true),
                    ]"
                />
            </x-flex>

            <template v-if="['percentage_of_margin_gross', 'percentage_of_margin_net'].includes(insurerData.pricing_type)">
                <x-flex xs3>
                    <x-currency-text-field
                        v-model="insurerData.margin_percentage"
                        class="required"
                        label="Percentage"
                        placeholder=" "
                        suffix="%"
                        :rules="[
                            v => ruleRequired(v, 'Percentage'),
                            v => ruleNumeric(v, false, false, true),
                            v => ruleLessThanOrEqualTo(v, 100, 'Percentage'),
                            v => ruleGreaterThan(v, 0, 'Percentage')
                        ]"
                    />
                </x-flex>

                <x-flex xs3>
                    <x-currency-text-field
                        v-model="insurerData.price"
                        label="Price"
                        placeholder=" "
                        :prefix="riskCurrency"
                        disabled
                    />
                </x-flex>
            </template>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-currency-text-field
                    data-test="brokerage"
                    v-model="insurerData.brokerage"
                    class="required"
                    label="Brokerage"
                    placeholder=" "
                    suffix="%"
                    :rules="[
                        v => ruleRequired(v, 'Brokerage'),
                        v => ruleNumeric(v, false, false, true),
                        v => ruleLessThanOrEqualTo(v, 100, 'Brokerage'),
                        v => ruleGreaterThan(v, 0, 'Brokerage')
                    ]"
                />
            </x-flex>

            <x-flex xs6>
                <x-select
                    v-model="insurerData.status"
                    :items="statuses"
                    label="Status"
                    item-text="name"
                    item-value="id"
                    placeholder=" "
                    append-icon="keyboard_arrow_down"
                    :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs6>
                <x-menu
                    v-model="dateInsurerFeedbackReceivedInput"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    max-width="300"
                    full-width
                >
                    <template v-slot:activator="{ on }">
                        <x-text-field
                            data-test="date_insurer_feedback_received"
                            v-model="insurerData.date_insurer_feedback_received"
                            label="Date insurer feedback received"
                            placeholder=" "
                            append-icon="event"
                            readonly
                            v-on="on"
                        />
                    </template>

                    <x-date-picker
                        v-model="insurerData.date_insurer_feedback_received"
                        @input="dateInsurerFeedbackReceivedInput = false"
                    />
                </x-menu>
            </x-flex>

            <x-flex xs6>
                <x-text-field
                    data-test="nbi_validity_period"
                    v-model="insurerData.nbi_validity_period_days"
                    label="NBI validity period"
                    suffix="days"
                    :rules="[
                        v => ruleMaxDecimalPlaces(v, 0, 'NBI validity period'),
                    ]"
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex>
                <x-textarea
                    data-test="feedback"
                    v-model="insurerData.feedback"
                    label="Feedback"
                    placeholder=" "
                />
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex>
                <x-textarea
                    data-test="subjectivities"
                    v-model="insurerData.subjectivities"
                    label="Subjectivities"
                    placeholder=" "
                />
            </x-flex>
        </x-layout>
    </div>
</template>

<script>
import rules from "../../../mixins/rules";
import TextEditor from "../../Shared/UIComponents/Editor/TextEditor.vue";

export default {
    name: 'MSTEntryInsurerInfo',
    components: {TextEditor},
    props: {
        availableInsurers: {
            type: Array,
            required: true
        },
        insurerData: {
            type: Object,
            required: true
        },
        securities: {
            type: Array,
            required: true
        },
        currencies: {
            type: Array,
            required: true
        },
        submission: {
            type: Object,
            required: true
        }
    },
    mixins: [rules],
    data() {
        return {
            statuses: [
                {
                    id: 'accepted',
                    name: 'Accepted'
                },
                {
                    id: 'declined',
                    name: 'Declined'
                },
                {
                    id: 'requested',
                    name: 'Requested'
                },
                {
                    id: 'offered',
                    name: 'Offered'
                },
                {
                    id: 'ntu',
                    name: 'NTU'
                },
            ],
            pricingTypes: [
                {
                    id: 'fixed_rate',
                    name: 'Fixed rate'
                },
                {
                    id: 'variable',
                    name: 'Variable'
                },
                {
                    id: 'flat_fee',
                    name: 'Flat fee'
                },
                {
                    id: 'percentage_of_margin_gross',
                    name: '% of Gross Margin'
                },
                {
                    id: 'percentage_of_margin_net',
                    name: '% of Net Margin'
                }
            ],
            dateInsurerFeedbackReceivedInput: false,
        }
    },
    computed: {
        selectedInsurer() {
            return this.availableInsurers.find(({ id }) => id === this.insurerData.insurer_company_id);
        },
        selectedSecurity() {
            return this.availableSecurities.find(({ id }) => id === this.insurerData.security?.id);
        },
        availableSecurities() {
            if (this.selectedInsurer) {
                return this.securities.filter(security => security.insurer_company.id === this.insurerData.insurer_company_id);
            }
            return [];
        },
        availableUnderwriters() {
            if (this.selectedInsurer) {
                return this.selectedInsurer.users;
            }
            return [];
        },
        strengthRating() {
            if (this.selectedSecurity) {
                return this.selectedSecurity.strength_rating + this.selectedSecurity.rating_suffix
            }
            return "";
        },
        indicatedLineSizeCurrency() {
            if (this.insurerData.indicated_line_size_currency_id) {
                return this.currencies.find(curr => curr.id === this.insurerData.indicated_line_size_currency_id)?.code;
            }
            return null;
        },
        riskCurrency() {
            if (this.currencies && this.submission) {
                const selectedCurrency = this.currencies.find(currency => this.submission.currency_id === currency.id) || {};
                return selectedCurrency.code || '';
            }
            return '';
        },
    },
    watch: {
        'insurerData.pricing_type': {
            deep: true,
            handler (val, oldVal) {
                if (val !== oldVal) {
                    Vue.set(this.insurerData, 'price', null);
                    Vue.set(this.insurerData, 'margin_percentage', null);
                }
            }
        },
        'insurerData.margin_percentage'() {
            if (this.insurerData.margin_percentage) {
                let margin = 0;

                if (this.insurerData.pricing_type === 'percentage_of_margin_gross') {
                    margin = this.submission.gross_margin
                }

                if (this.insurerData.pricing_type === 'percentage_of_margin_net') {
                    margin = this.submission.net_margin;
                }

                Vue.set(this.insurerData, 'price', this.insurerData.margin_percentage / 100 * margin);
            }
        },
        'insurerData.insurer_company_id': {
            deep: true,
            handler (val, oldVal) {
                if (val !== oldVal) {
                    this.$emit('resetInsurer', val);
                }
            }
        },
    }
}
</script>
